import { PencilIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { postSignIn } from '@/api/auth';
import { patchCurrentUserProfile } from '@/api/Terminal/UserAccount';
import type { IPatchCurrentUserProfilePayload } from '@/api/Terminal/UserAccount/types';
import AccentTitle from '@/components/UI/AccentTitle';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import { SingleEditableField } from '@/components/UI/Form/EditableField';
import Input from '@/components/UI/Form/Input';
import Modal from '@/components/UI/Modal';
import { ReactQueryKey } from '@/constants/ReactQueryKeys';
import useUpload from '@/hooks/useUpload';
import { authSelector, useAppDispatch } from '@/store';
import { tryCurrentUser } from '@/store/Auth';
import { notifyUser } from '@/store/Toast';
import { getInitials, sanitizeResponseError } from '@/utils';

export default function Profile() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data: currentUserData } = useSelector(authSelector);
  const { privileges } = currentUserData || { privileges: [] };
  const { uploadFile: uploadProfilePicture } = useUpload();
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyPasswordError, setVerifyPasswordError] = useState('');
  const [newEmailData, setNewEmailData] = useState();
  const [verifyUser, setVerifyUser] = useState(false);

  const updateCurrentUserProfile = useMutation(
    (data: Partial<IPatchCurrentUserProfilePayload>) =>
      patchCurrentUserProfile({
        currentUserId: currentUserData.id,
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_CURRENT_USER]);
        dispatch(tryCurrentUser());
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Profile updated successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        queryClient.invalidateQueries([ReactQueryKey.GET_CURRENT_USER]);
        dispatch(tryCurrentUser());
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const verifyUserSignIn = useMutation((payload: { email: string; password: string }) => postSignIn(payload.email, payload.password), {
    onSuccess: () => {
      if (newEmailData) {
        updateCurrentUserProfile.mutate(newEmailData!);
      }
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Password is not valid',
          toastType: 'failed',
        })
      );
    },
  });

  const updateUserProfileImage = useCallback(
    (profileImage: { key: string; type: string; size: number }) => {
      updateCurrentUserProfile.mutate({ profileImage });
    },
    [updateCurrentUserProfile.mutate]
  );

  const handleUpdateEmail = () => {
    if (verifyPassword.trim().length === 0) {
      setVerifyPasswordError('Required');
      return;
    }
    verifyUserSignIn.mutate({
      email: currentUserData.email,
      password: verifyPassword,
    });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    uploadProfilePicture({
      file: file,
      resource: 'users',
      resourceId: currentUserData.id,
      applyData: updateUserProfileImage,
    });
  };

  return (
    <>
      {verifyUser && (
        <Modal>
          <h2 className='m-0 capitalize'>Change Email Address</h2>
          <p className='my-5'>Account password is required to change the email address.</p>
          <Input
            type='password'
            name='password'
            changed={(e) => {
              setVerifyPassword(e.target.value);
              if (e.target.value.length > 0) {
                setVerifyPasswordError('');
              }
            }}
            error={verifyPasswordError}
          />
          <div className='flex justify-end gap-4 mt-5'>
            <Button
              label='Cancel'
              disabled={updateCurrentUserProfile.isLoading}
              btnStyle='btn-neutral'
              onClick={() => setVerifyUser(false)}
            />
            <Button
              label={updateCurrentUserProfile.isLoading ? 'Processing...' : 'Submit'}
              disabled={updateCurrentUserProfile.isLoading}
              btnStyle='primary'
              onClick={handleUpdateEmail}
            />
          </div>
        </Modal>
      )}
      <div className='divide-y divide-gray-200'>
        <AccentTitle title='Profile' />
        <div className='px-5'>
          <dl className='divide-y divide-gray-200'>
            <div className='py-5 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5'>
              <dt className='text-sm font-medium text-gray-500'>Profile Picture</dt>
              <dd className='mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <span className='flex-grow'>
                  <ImageCircularAvatar imageUrl={currentUserData.profileImageUrl} fallbackText={getInitials(currentUserData.name)} />
                </span>
                <span className='ml-4 flex items-center'>
                  <div className='relative flex items-center cursor-pointer hover:bg-blue-gray-50 '>
                    <label
                      htmlFor='profileImage'
                      className='relative text-sm font-medium text-primaryColor-normal hover:text-primaryColor-dark pointer-events-none'
                    >
                      <PencilIcon className='h-5 w-5 text-neutralColor-light hover:text-neutralColor-normal cursor-pointer' />
                      <span className='sr-only'>Profile picture</span>
                    </label>
                    <input
                      id='profileImage'
                      name='profileImage'
                      type='file'
                      className='absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md'
                      accept='.jpg, .jpeg, .png'
                      onChange={handleFileChange}
                    />
                  </div>
                </span>
              </dd>
            </div>
            <div className='py-5 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Name</dt>
              <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <SingleEditableField
                  name='name'
                  type='input'
                  inputValue={currentUserData.name}
                  permission='N/A'
                  maxLength={50}
                  minLength={6}
                  onSubmit={updateCurrentUserProfile.mutate}
                  isLoading={updateCurrentUserProfile.isLoading}
                  isSuccess={updateCurrentUserProfile.isSuccess}
                />
              </dd>
            </div>

            <div className='py-5 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5'>
              <dt className='text-sm font-medium text-gray-500'>Email</dt>
              <dd className='mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <SingleEditableField
                  name='email'
                  type='input'
                  inputValue={currentUserData.email}
                  permission='N/A'
                  maxLength={50}
                  minLength={6}
                  onSubmit={(d) => {
                    setVerifyUser(true);
                    setNewEmailData(d);
                  }}
                  isLoading={updateCurrentUserProfile.isLoading}
                  isSuccess={updateCurrentUserProfile.isSuccess}
                />
              </dd>
            </div>
            <div className='py-5 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200'>
              <dt className='text-sm font-medium text-gray-500'>Organizational Title</dt>
              <dd className='mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <span className='flex-grow'>{currentUserData.title}</span>
              </dd>
            </div>
            <div className='py-5 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5'>
              <dt className='text-sm font-medium text-gray-500'>Permissions</dt>
              <dd className='mt-1 flex flex-wrap gap-2 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {privileges.map((privilege, _index) => {
                  return (
                    <span key={privilege} className='capitalize'>
                      <Badge badgeType='neutral' text={privilege.toLowerCase().replaceAll('_', ' ')} />
                    </span>
                  );
                })}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
}
